import { supabase } from '../supabaseClient';
import { ungzip } from 'pako';
export type ExportMode = 'single' | 'onehot';

function downloadBlob(content: string, filename: string, contentType = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeCsv(val: string): string {
  if (val.includes(',') || val.includes('"') || val.includes('\n')) {
    return '"' + val.replace(/"/g, '""') + '"';
  }
  return val;
}

export function exportJobCsv(jobId: string, results: { text: string; category: string }[], categories: string[], mode: ExportMode) {
  if (mode === 'single') {
    const header = ['text', 'category'];
    const rows = results.map(r => [r.text, r.category]);
    const csv = [header, ...rows].map(row => row.map(v => escapeCsv(String(v ?? ''))).join(',')).join('\n');
    downloadBlob(csv, `job_${jobId}.csv`);
    return;
  }
  // onehot
  const uniq = Array.from(new Set(categories));
  const header = ['text', ...uniq];
  const rows = results.map(r => {
    const hot = uniq.map(c => (r.category === c ? '1' : '0'));
    return [r.text, ...hot];
  });
  const csv = [header, ...rows].map(row => row.map(v => escapeCsv(String(v ?? ''))).join(',')).join('\n');
  downloadBlob(csv, `job_${jobId}_onehot.csv`);
}

export function exportProfileCsv(profileId: string, allResults: { text: string; category: string }[], categories: string[], mode: ExportMode) {
  if (mode === 'single') {
    const header = ['text', 'category'];
    const rows = allResults.map(r => [r.text, r.category]);
    const csv = [header, ...rows].map(row => row.map(v => escapeCsv(String(v ?? ''))).join(',')).join('\n');
    downloadBlob(csv, `profile_${profileId}.csv`);
    return;
  }
  const uniq = Array.from(new Set(categories));
  const header = ['text', ...uniq];
  const rows = allResults.map(r => {
    const hot = uniq.map(c => (r.category === c ? '1' : '0'));
    return [r.text, ...hot];
  });
  const csv = [header, ...rows].map(row => row.map(v => escapeCsv(String(v ?? ''))).join(',')).join('\n');
  downloadBlob(csv, `profile_${profileId}_onehot.csv`);
} 

// ------- Storage-based exports -------
async function downloadAndParseNdjsonGz(resultUri: string): Promise<{ text: string; category: string }[]> {
  const { data: fileData, error } = await supabase.storage.from('jobs').download(resultUri);
  if (error) throw error;
  const buf = await fileData.arrayBuffer();
  const text = new TextDecoder('utf-8').decode(ungzip(new Uint8Array(buf)));
  const lines = text.split('\n').filter(Boolean);
  const rows = lines.map((l) => {
    try { return JSON.parse(l); } catch { return null as any; }
  }).filter((r: any) => r && typeof r.text === 'string' && typeof r.category === 'string');
  return rows as { text: string; category: string }[];
}

export async function exportResultsFromStorageCsv(jobId: string, resultUri: string, mode: ExportMode) {
  const rows = await downloadAndParseNdjsonGz(resultUri);
  const categories = Array.from(new Set(rows.map(r => r.category)));
  return exportJobCsv(jobId, rows, categories, mode);
}

export async function exportResultsFromStorageJsonl(jobId: string, resultUri: string) {
  const { data: fileData, error } = await supabase.storage.from('jobs').download(resultUri);
  if (error) throw error;
  const blob = new Blob([fileData], { type: 'application/gzip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `job_${jobId}.ndjson.gz`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}